import { useMemo, useState } from 'react'
import { bodyMarketInventory } from '../../lib/bodyMarketInventory.js'
import { REGISTRY_ENTRIES, registryCardFor } from '../../lib/bodyRegistryIndex.js'
import ledger from '../../lib/bodyEnumerationLedger.json'
import styles from './BodyPerfection.module.css'
import SeasonTitleAudit from './SeasonTitleAudit.jsx'

const count = value => value.toLocaleString('en-US')
const PAGE = 25

export default function BodyMarketInventory({ data, status, complete, onPickBody, executive = false }) {
  const [shown, setShown] = useState(PAGE)
  const [bodyLimit, setBodyLimit] = useState(PAGE), [labelLimit, setLabelLimit] = useState(PAGE)
  const ready = status === 'done' && complete === true
  const inventory = useMemo(() => ready ? bodyMarketInventory({ records: data || [],
    registry: REGISTRY_ENTRIES, ledger, resolveCard: registryCardFor }) : null, [ready, data])
  const missing = inventory?.rows.filter(row => row.noHoldings) || []
  return (
    <section className={`card ${styles.section}`} aria-label="Championship market inventory">
      <p className={styles.head}>Championship market inventory</p>
      {!inventory ? <p className={styles.note} role={status === 'error' ? 'alert' : undefined}>
        {status === 'error' ? 'Inventory unavailable: the complete championship read failed.'
          : 'Waiting for the complete championship inventory before measuring coverage.'}
      </p> : <>
        <p className={styles.big} data-market-records>
          {count(inventory.summary.knownRecords)} known championship records. Full market coverage remains unproven.
        </p>
        <p className={styles.note} data-market-directory>
          The existing directory lists {count(inventory.summary.registryOrganizations)} organizations across {count(inventory.summary.registryCardRows)} cards.
          {' '}{count(inventory.summary.noHoldings)} have no active matched records; {count(inventory.summary.withoutEnumeration)} have no linked published-title enumeration.
          Organizations operating in multiple states count once.
        </p>
        <p className={styles.note}>
          Every season needs its own expected championship list by sport, division, classification and category.
          Matching a published count alone does not establish that every title is present. These gaps require source discovery.
        </p>
        <p className={styles.note} data-market-accounting>
          Records accounted for: {count(inventory.summary.accountedRecords)} of {count(inventory.summary.knownRecords)}.
          {' '}{count(inventory.summary.unmatchedRecords)} name a body outside this directory;
          {' '}{count(inventory.summary.unattributedRecords)} lack a usable body;
          {' '}{count(inventory.summary.conflictingRecords)} name conflicting bodies.
          {' '}{count(inventory.unlinkedEnumeration.length)} published enumerations still need a confirmed directory match.
          {!inventory.summary.reconciles && ' Reconciliation failed.'}
        </p>
        <p className={styles.note}>
          {count(inventory.readiness.active)} active records; {count(inventory.readiness.inactive)} retired or out of scope.
          {' '}{count(inventory.readiness.unassigned)} active records lack an owner.
          {' '}{count(inventory.readiness.dates.projected || 0)} projected dates;
          {' '}{count(inventory.readiness.dates.conflict || 0)} date conflicts;
          {' '}{count((inventory.readiness.dates.unknown || 0) + (inventory.readiness.dates['needs-source'] || 0))} need a date or source.
          {' '}A cited source is not counted as independently verified.
        </p>
        <details><summary className={styles.more}>All registered governing bodies ({count(inventory.rows.length)})</summary>
          <ul className={styles.list}>{inventory.rows.slice(0, bodyLimit).map(body => <li key={body.id} className={styles.row}>
            <span className={styles.name}>{body.name}</span>
            <p className={styles.why}>{count(body.readiness.active)} active records · {count(body.readiness.unassigned)} unassigned · {count(body.readiness.dates.projected || 0)} projected · {count(body.readiness.dates.conflict || 0)} date conflicts. Seasonal title slate requires reconciliation.</p>
            <button className={styles.more} onClick={() => onPickBody(body.name)}>Open {body.name}</button>
          </li>)}</ul>
          {bodyLimit < inventory.rows.length && <button className={styles.more} onClick={() => setBodyLimit(n => n + PAGE)}>Show more registered bodies</button>}
        </details>
        <details><summary className={styles.more}>Unresolved governing-body labels ({count(inventory.unmatched.length)})</summary>
          <p className={styles.note}>These may be missing organizations, aliases or fused names. They remain visible until resolved; each label is not assumed to be a distinct governing body.</p>
          <ul className={styles.list}>{inventory.unmatched.slice(0, labelLimit).map(body => <li key={body.name} className={styles.row}>
            <span className={styles.name}>{body.name}</span><p className={styles.why}>{count(body.count)} records · {count(body.readiness.unassigned)} active unassigned. Confirm the sanctioning body, its official source and its seasonal title slate.</p>
            <button className={styles.more} onClick={() => onPickBody(body.name)}>Review {body.name}</button>
          </li>)}</ul>
          {labelLimit < inventory.unmatched.length && <button className={styles.more} onClick={() => setLabelLimit(n => n + PAGE)}>Show more unresolved labels</button>}
        </details>
        {executive && <SeasonTitleAudit />}
        <details>
          <summary className={styles.more} data-market-missing-toggle>Open governing bodies with no active championships ({count(missing.length)})</summary>
          <ul className={styles.list}>
            {missing.slice(0, shown).map(body => <li key={body.id} className={styles.row} data-market-missing>
              <span className={styles.name}>{body.name}</span>
              <p className={styles.why}>{body.unmeasured ? 'Official championship list needs enumeration.'
                : 'Published count evidence exists. Match the individual titles and season before creating records.'}</p>
              {body.outOfScope > 0 && <p className={styles.why}>{count(body.outOfScope)} records marked Out of Scope remain in the inventory.</p>}
              <button type="button" className={styles.more} onClick={() => onPickBody(body.name)}>
                Open {body.name}
              </button>
            </li>)}
          </ul>
          {missing.length > shown && <button type="button" className={styles.more} onClick={() => setShown(n => n + PAGE)}>
            Show more governing bodies ({count(missing.length - shown)} remaining)
          </button>}
        </details>
      </>}
    </section>
  )
}
